import React from 'react';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { formatCurrencyForCSV as formatCurrency } from '../../lib/helpers/formatters';
import { BtnOutlineOrange } from '../../styled-components';

const ExportExtraInvoiceCSVField = ({ data }) => {
  const [t] = useTranslation();

  const type = Object.hasOwn(data, 'invoices')
    ? 'invoices'
    : Object.hasOwn(data, 'contracts')
    ? 'contracts'
    : Object.hasOwn(data, 'insurances')
    ? 'insurances'
    : Object.hasOwn(data, 'recurrentContracts')
    ? 'recurrentContracts'
    : Object.hasOwn(data, 'signals')
    ? 'signals'
    : '';

  if (type === '') return '';

  return (
    <CSVExport data={{ data }} type={type}>
      <BtnOutlineOrange
        type="button"
        className="uppercase"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {t('COMMON:CSV_EXPORT')}
      </BtnOutlineOrange>
    </CSVExport>
  );
};

const getInvoicesCSVData = (data) => {
  if (data?.invoices && data?.invoices?.length > 0) {
    const CSVData = [
      [
        'NOME FORNITORE',
        'PARTITA IVA',
        'NUMERO FATTURA',
        'CLIENTE',
        'RESPONSABILE',
        'DATA FATTURA',
        'MESE RENDICONTAZIONE',
        'IMPONIBILE',
        'IMPORTO FATTURATO',
        'IMPORTO DA INCASSARE',
      ],
    ];

    const amount = data.invoices.reduce((acc, { extraNet }) => {
      return acc + +extraNet;
    }, 0);

    data.invoices.forEach(
      ({
        supplier,
        vatCode,
        number,
        customer,
        admin,
        date,
        accountability,
        net,
        extraNet,
        gross,
      }) => {
        CSVData.push([
          supplier.businessName,
          vatCode,
          number,
          customer,
          admin.businessName,
          moment(date).format('DD/MM/yyyy'),
          accountability ? moment(accountability).format('MM/yyyy') : '',
          formatCurrency(+net),
          formatCurrency(+gross),
          formatCurrency(+extraNet),
        ]);
      }
    );

    CSVData.push(['', '', '', '', '', '', '', '', '', formatCurrency(+amount)]);

    return CSVData || [];
  }
};

const getInsurancesCSVData = (data) => {
  if (data?.insurances && data?.insurances?.length > 0) {
    const CSVData = [
      [
        'RAMI',
        'CODICE POLIZZA',
        'ETICHETTA POLIZZA',
        'REFERENTE',
        'CODICE FISCALE REFERENTE',
        'FORNITORE',
        'CODICE FISCALE FORNITORE',
        'TIPO AGENZIA',
        'CLIENTE',
        'P.IVA / CODICE FISCALE CLIENTE',
        'DATA INIZIO',
        'DATA FINE',
        'PREMIO LORDO',
        'PREMIO NETTO',
        'ROY LA PANORAMICA',
        'PROFITTO LA PANORAMICA',
        'PROFITTO SUB AGENZIA',
      ],
    ];

    const grossTotal = data.insurances.reduce((acc, { gross }) => {
      return acc + +gross;
    }, 0);
    const netTotal = data.insurances.reduce((acc, { net }) => {
      return acc + +net;
    }, 0);
    const shareToInvoiceTotal = data.insurances.reduce(
      (acc, { shareToInvoice }) => {
        return acc + +shareToInvoice;
      },
      0
    );
    const panoramicaShareTotal = data.insurances.reduce(
      (acc, { panoramicaShare }) => {
        return acc + +panoramicaShare;
      },
      0
    );
    const adminShareTotal = data.insurances.reduce((acc, { adminShare }) => {
      return acc + +adminShare;
    }, 0);

    data.insurances.forEach(
      ({
        branch,
        policy_code,
        insurance_code,
        admin_vat_cf,
        start_date,
        end_date,
        gross,
        net,
        shareToInvoice,
        panoramicaShare,
        adminShare,
        adminInfo,
        customer,
        customerType,
        agencyType,
        supplierInfo,
      }) => {
        CSVData.push([
          branch,
          policy_code,
          insurance_code,
          adminInfo?.businessName || '',
          admin_vat_cf,
          supplierInfo?.businessName || '',
          supplierInfo?.taxCode || '',
          agencyType || '',
          customer && customerType === 'condos'
            ? customer.name
            : customer &&
              customerType === 'customerExtra' &&
              customer.type === 'AZIENDA'
            ? customer.businessName
            : customer &&
              customerType === 'customerExtra' &&
              customer.type === 'PRIVATO'
            ? customer.name + ' ' + customer.surname
            : '',
          customer && customerType === 'condos'
            ? customer.vatCode
            : customer && customerType === 'customerExtra'
            ? customer.taxCode
            : '',
          moment(start_date).format('DD/MM/yyyy'),
          moment(end_date).format('DD/MM/yyyy'),
          formatCurrency(gross),
          formatCurrency(net),
          formatCurrency(shareToInvoice),
          formatCurrency(panoramicaShare),
          formatCurrency(adminShare),
        ]);
      }
    );

    CSVData.push([
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      formatCurrency(+grossTotal),
      formatCurrency(+netTotal),
      formatCurrency(+shareToInvoiceTotal),
      formatCurrency(+panoramicaShareTotal),
      formatCurrency(+adminShareTotal),
    ]);

    return CSVData || [];
  }
};

const getContractsCSVData = (data) => {
  if (data?.contracts && data?.contracts?.length > 0) {
    const CSVData = [
      [
        'POD/PDR',
        'NOME CLIENTE',
        'PARTITA IVA RESPONSABILE',
        'PARTITA IVA CLIENTE',
        'DATA CREAZIONE',
        'DATA ATTIVAZIONE',
        'CONSUMI DA CONTRATTO',
        'NOME PRODOTTO',
        'ADMIN',
        'IMPORTO DA INCASSARE',
      ],
    ];

    const amount = data.contracts.reduce((acc, { extraNet }) => {
      return acc + +extraNet;
    }, 0);

    data.contracts.forEach(
      ({
        pod_pdr,
        condo_name,
        admin,
        condo_admin_cf,
        condo_vat_cf,
        contract_signing_date,
        activation_start_date,
        contractual_consumption,
        product_name,
        extraNet,
      }) => {
        CSVData.push([
          pod_pdr,
          condo_name,
          condo_admin_cf,
          condo_vat_cf,
          moment(contract_signing_date).format('DD/MM/yyyy'),
          moment(activation_start_date).format('DD/MM/yyyy'),
          contractual_consumption,
          product_name,
          admin.businessName,
          formatCurrency(+extraNet),
        ]);
      }
    );

    CSVData.push(['', '', '', '', '', '', '', '', '', formatCurrency(+amount)]);

    return CSVData || [];
  }
};

const getRecurrentContractsCSVData = (data) => {
  if (data?.recurrentContracts && data?.recurrentContracts?.length > 0) {
    const CSVData = [
      [
        'POD/PDR',
        'NOME CLIENTE',
        'PARTITA IVA RESPONSABILE',
        'PARTITA IVA CLIENTE',
        'DATA CREAZIONE',
        'DATA ATTIVAZIONE',
        'CONSUMI DA CONTRATTO',
        'NOME PRODOTTO',
        'ADMIN',
        'PERCENTUALE COMPENSO',
        'DATA COMPENSO',
        'ACCONTO',
        'IMPORTO ANNUO',
        'IMPORTO DA INCASSARE',
      ],
    ];

    const amount = data.recurrentContracts.reduce((acc, { extraNet }) => {
      return acc + +extraNet;
    }, 0);

    let amountExtraShareStart = 0;
    let amountExtraShareMid = 0;

    data.recurrentContracts.forEach(
      ({
        pod_pdr,
        condo_name,
        admin,
        condo_admin_cf,
        condo_vat_cf,
        contract_signing_date,
        activation_start_date,
        contractual_consumption,
        product_name,
        percentage,
        dateFee,
        extraNet,
        energy_contract,
        typeContract,
        typeFee,
        actual_consumption,
      }) => {
        let extraShareStart = 0;
        let extraShareMid = 0;
        if (
          typeContract === 'gas' &&
          typeFee === 'actual' &&
          [
            'EON_GAS_CERTO_NEW',
            'EON_GAS_CERTO_PREMIUM_NEW',
            'EON_DINAMICA_SEMPLICE_PIU',
            'DUFERCO_GAS_GIOVE',
            'DUFERCO_GAS_NETTUNO',
            'DUFERCO_GAS_MARTE',
            'INDEX_BUSINESS_SCONTO_6',
            'INDEX_BUSINESS_SCONTO_2',
            'DUFERCO_GAS_VENERE',
            'GAS_PLATINO_SUPER_BOLLETTA',
            'GAS_DIAMANTE',
            'GAS_ORO',
            'GAS_ARGENTO',
            'GEOFLEX_COND_115',
            'GEOFLEX_COND_105',
            'GEOFLEX_COND_95',
            'GEOFLEX_COND_85',
            'GEOFLEX_COND_75',
            'GEOFLEX_COND_65',
            'GEOFLEX_COND_55',
            'GEOFLEX_COND_45',
            'GEOFLEX_COND_35',
            'GEOFLEX_COND_25',
            'GEOFLEX_COND_15',
          ].includes(product_name)
        ) {
          extraShareStart =
            energy_contract.recurring_value *
            contractual_consumption *
            (percentage / 100);

          extraShareMid = energy_contract.recurring_value * actual_consumption;

          amountExtraShareStart += extraShareStart;
          amountExtraShareMid += extraShareMid;
        }

        return CSVData.push([
          pod_pdr,
          condo_name,
          condo_admin_cf,
          condo_vat_cf,
          moment(contract_signing_date).format('DD/MM/yyyy'),
          moment(activation_start_date).format('DD/MM/yyyy'),
          contractual_consumption,
          product_name,
          admin.businessName,
          percentage,
          moment(dateFee).format('DD/MM/yyyy'),
          typeContract === 'gas' &&
          typeFee === 'actual' &&
          [
            'EON_GAS_CERTO_NEW',
            'EON_GAS_CERTO_PREMIUM_NEW',
            'EON_DINAMICA_SEMPLICE_PIU',
            'DUFERCO_GAS_GIOVE',
            'DUFERCO_GAS_NETTUNO',
            'DUFERCO_GAS_MARTE',
            'INDEX_BUSINESS_SCONTO_6',
            'INDEX_BUSINESS_SCONTO_2',
            'DUFERCO_GAS_VENERE',
            'GAS_PLATINO_SUPER_BOLLETTA',
            'GAS_DIAMANTE',
            'GAS_ORO',
            'GAS_ARGENTO',
            'GEOFLEX_COND_115',
            'GEOFLEX_COND_105',
            'GEOFLEX_COND_95',
            'GEOFLEX_COND_85',
            'GEOFLEX_COND_75',
            'GEOFLEX_COND_65',
            'GEOFLEX_COND_55',
            'GEOFLEX_COND_45',
            'GEOFLEX_COND_35',
            'GEOFLEX_COND_25',
            'GEOFLEX_COND_15',
          ].includes(product_name)
            ? formatCurrency(+extraShareMid)
            : '',
          typeContract === 'gas' &&
          typeFee === 'actual' &&
          [
            'EON_GAS_CERTO_NEW',
            'EON_GAS_CERTO_PREMIUM_NEW',
            'EON_DINAMICA_SEMPLICE_PIU',
            'DUFERCO_GAS_GIOVE',
            'DUFERCO_GAS_NETTUNO',
            'DUFERCO_GAS_MARTE',
            'INDEX_BUSINESS_SCONTO_6',
            'INDEX_BUSINESS_SCONTO_2',
            'DUFERCO_GAS_VENERE',
            'GAS_PLATINO_SUPER_BOLLETTA',
            'GAS_DIAMANTE',
            'GAS_ORO',
            'GAS_ARGENTO',
            'GEOFLEX_COND_115',
            'GEOFLEX_COND_105',
            'GEOFLEX_COND_95',
            'GEOFLEX_COND_85',
            'GEOFLEX_COND_75',
            'GEOFLEX_COND_65',
            'GEOFLEX_COND_55',
            'GEOFLEX_COND_45',
            'GEOFLEX_COND_35',
            'GEOFLEX_COND_25',
            'GEOFLEX_COND_15',
          ].includes(product_name)
            ? formatCurrency(+extraShareStart)
            : '',
          formatCurrency(+extraNet),
        ]);
      }
    );

    CSVData.push([
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      formatCurrency(+amount),
    ]);

    return CSVData || [];
  }
};

const getSignalsCSVData = (data) => {
  if (data?.signals && data?.signals?.length > 0) {
    const signalTypeCode = data.signals[0]?.signalTypeInfo?.code;
    const amount = data.signals.reduce((acc, { shareToInvoice }) => {
      return acc + +shareToInvoice;
    }, 0);

    if (signalTypeCode === 'CONDO') {
      const CSVData = [
        [
          'SEGNALATORE',
          'P.IVA/CODICE FISCALE SEGNALATORE',
          'NOME CONDOMINIO',
          'P.IVA/CODICE FISCALE CONDOMINIO',
          'DATA RENDICONTAZIONE',
          'RESPONSABILE DI ZONA',
          'COMMERCIALE 1',
          'COMMERCIALE 2',
          'TOTALE FATTURA',
          'IMPONIBILE EXTRA',
        ],
      ];

      data.signals.forEach(
        ({
          signalmanProfile,
          condoInfo,
          accountabilityDate,
          commercial,
          commercial2,
          commercial3,
          estimate,
          shareToInvoice,
        }) => {
          CSVData.push([
            signalmanProfile?.businessName || '',
            signalmanProfile?.fiscalCode || '',
            condoInfo?.name || '',
            condoInfo?.vatCode || '',
            moment(accountabilityDate).format('DD/MM/yyyy'),
            commercial?.name || '',
            commercial2?.name || '',
            commercial3?.name || '',
            formatCurrency(+estimate),
            formatCurrency(+shareToInvoice),
          ]);
        }
      );

      CSVData.push([
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        formatCurrency(+amount),
      ]);
      return CSVData || [];
    } else if (signalTypeCode === 'RENTALS_SALES') {
      const CSVData = [
        [
          'SEGNALATORE',
          'P.IVA/CODICE FISCALE SEGNALATORE',
          'DATA RENDICONTAZIONE',
          'RESPONSABILE DI ZONA',
          'COMMERCIALE 1',
          'COMMERCIALE 2',
          'TOTALE FATTURA',
          'IMPONIBILE EXTRA',
        ],
      ];

      data.signals.forEach(
        ({
          signalmanProfile,
          accountabilityDate,
          commercial,
          commercial2,
          commercial3,
          estimate,
          shareToInvoice,
        }) => {
          CSVData.push([
            signalmanProfile?.businessName || '',
            signalmanProfile?.fiscalCode || '',
            moment(accountabilityDate).format('DD/MM/yyyy'),
            commercial?.name || '',
            commercial2?.name || '',
            commercial3?.name || '',
            formatCurrency(+estimate),
            formatCurrency(+shareToInvoice),
          ]);
        }
      );

      CSVData.push([
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        formatCurrency(+amount),
      ]);
      return CSVData || [];
    } else if (signalTypeCode === 'SPECIAL_JOBS') {
      const CSVData = [
        [
          'SEGNALATORE',
          'P.IVA/CODICE FISCALE SEGNALATORE',
          'DATA RENDICONTAZIONE',
          'RESPONSABILE DI ZONA',
          'COMMERCIALE 1',
          'COMMERCIALE 2',
          'TOTALE FATTURA',
          'IMPONIBILE EXTRA',
        ],
      ];

      data.signals.forEach(
        ({
          signalmanProfile,
          accountabilityDate,
          commercial,
          commercial2,
          commercial3,
          estimate,
          shareToInvoice,
        }) => {
          CSVData.push([
            signalmanProfile.businessName,
            signalmanProfile.fiscalCode,
            moment(accountabilityDate).format('DD/MM/yyyy'),
            commercial.name,
            commercial2.name,
            commercial3.name,
            formatCurrency(+estimate),
            formatCurrency(+shareToInvoice),
          ]);
        }
      );

      CSVData.push([
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        '-',
        formatCurrency(+amount),
      ]);
      return CSVData || [];
    }
  }
};

const getCSVData = ({ data = {} }, type) => {
  const strategy = {
    invoices: getInvoicesCSVData,
    insurances: getInsurancesCSVData,
    contracts: getContractsCSVData,
    recurrentContracts: getRecurrentContractsCSVData,
    signals: getSignalsCSVData,
  };

  return strategy[type](data);
};

const CSVExport = ({ data = {}, type, children }) => {
  const csvData = getCSVData(data, type) || [];

  return (
    <CSVLink
      data={csvData}
      filename={
        data?.data?.type === 'insurances'
          ? `Panoramica_Invoice_${data.data._id.toUpperCase()}.csv`
          : `Extra_Invoice_${data.data._id.toUpperCase()}.csv`
      }
      separator={';'}
    >
      {children}
    </CSVLink>
  );
};

export default ExportExtraInvoiceCSVField;
